import {
  ICD_REASON,
  MUSCOSKELETAL_RESILIENCE,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  REHAB_ADMISSION,
  REHAB_DIRECTIVE_TYPE,
  REHAB_FOCUS,
  SPECIAL_REQUIREMENTS,
  TRANSPORTATION_SUBTYPE,
  TRANSPORTATION_TYPE,
  TYPE_OF_REHAB,
} from "core/consts";
import {
  createErrorMap,
  ICDCodeSchema,
  IKNumberSchema,
  INFECTIONS_CHARACTER_LIMIT,
  predicamentSchema,
  predicamentYesNoUnknownSchema,
  unixDateSchema,
  ValidationMessagesMap,
} from "core/schemas/shared";
import { GKVMedicalFormData } from "core/types";
import {
  beeintraechtigungMap,
  OPERATION_INDEXES,
} from "onprem/mapping/mapFormToRehabRequest";
import Translations from "translations/types";
import { z } from "zod";
import { isFilled, stripEmptyFields, toNullishNumber } from "./utils";

type GKVMedicalFormSchema = ReturnType<typeof createBaseGKVMedicalFormSchema>;

export const PER_ITEM_ERRORS = "perItemErrors" as const;

function createValidationMessages(
  translations: Translations,
): ValidationMessagesMap<GKVMedicalFormSchema> {
  const createTooLongMessage = (maxLength: number | bigint) =>
    translations.patientForms.characterLimitValidation({
      numberMost: maxLength.toString(),
    });

  const createTooShortMessage = (minLength: number | bigint) =>
    translations.patientForms.characterMinValidation({
      numberMinimum: minLength.toString(),
    });

  return {
    gender: {
      required: translations.patientForms.generalForm.fieldRequired,
    },
    patient_first_name: {
      required:
        translations.patientForms.medicalForm.sectionOne.firstNameRequired,
      tooLong: createTooLongMessage,
    },
    patient_last_name: {
      required:
        translations.patientForms.medicalForm.sectionOne.lastNameRequired,
      tooLong: createTooLongMessage,
    },
    patient_birthdate: {
      required:
        translations.patientForms.medicalForm.sectionOne.dateOfBirthRequired,
    },
    admission_date: {
      required: translations.patientForms.fieldRequiredValidation,
    },
    discharge_date: {
      required: translations.patientForms.fieldRequiredValidation,
    },
    insurance_number: {
      required:
        translations.patientForms.medicalForm.sectionOne
          .insuranceNumberRequired,
      tooShort: createTooShortMessage,
      tooLong: createTooLongMessage,
    },
    doctor_in_charge_in_hospital_first_name: {
      required:
        translations.patientForms.medicalForm.sectionOne.firstNameRequired,
      tooLong: createTooLongMessage,
    },
    doctor_in_charge_in_hospital_last_name: {
      required:
        translations.patientForms.medicalForm.sectionOne.lastNameRequired,
      tooLong: createTooLongMessage,
    },
    singer_assessment_date: {
      required: translations.patientForms.fieldRequiredValidation,
    },
    doctor_exam_date: {
      required: translations.patientForms.fieldRequiredValidation,
    },
    reha_capacity_partial: {
      invalid: translations.patientForms.fieldRequiredValidation,
      required: translations.patientForms.fieldRequiredValidation,
      tooShort: createTooShortMessage,
    },
    default: {
      required: translations.patientForms.fieldRequiredValidation,
      tooLong: createTooLongMessage,
      tooShort: createTooShortMessage,
      invalidUnion: translations.patientForms.generalForm.fieldRequired,
    },
  };
}

export function createBaseGKVMedicalFormSchema(translations: Translations) {
  // ICD rows 1–5
  const icdFields = Object.fromEntries(
    [1, 2, 3, 4, 5].flatMap((i) => {
      const isFirst = i === 1;

      return [
        [
          `icd_code_${i}_diagnosis`,
          isFirst ? z.string().max(150) : z.string().max(150).nullish(),
        ],
        [
          `icd_code_${i}_code`,
          isFirst ? ICDCodeSchema : ICDCodeSchema.nullish(),
        ],
        [
          `icd_code_${i}_reason`,
          isFirst
            ? z.nativeEnum(ICD_REASON, {
                errorMap: () => ({
                  message: translations.patientForms.generalForm.fieldRequired,
                }),
              })
            : z
                .nativeEnum(ICD_REASON, {
                  errorMap: () => ({
                    message:
                      translations.patientForms.generalForm.fieldRequired,
                  }),
                })
                .nullish(),
        ],
      ];
    }),
  );

  // operation entries
  const operationFields = Object.fromEntries(
    Object.values(OPERATION_INDEXES).flatMap((index) => [
      [`operation_${index}_date`, unixDateSchema.nullish()],
      [`operation_${index}_ops`, z.array(z.string()).nullish()],
      [
        `operation_${index}_wound_closed`,
        z
          .union([
            z.literal(PREDICAMENT_STATE_YES),
            z.literal(PREDICAMENT_STATE_NO),
          ])
          .nullish(),
      ],
    ]),
  );

  // singer assessments
  // derive singer assessment fields from your map:
  //  - key  = 'food', 'dressing_and_undressing', …
  //  - value = '01', '02', … used for sorting
  const singerAssessmentFields = Object.entries(beeintraechtigungMap)
    .sort(([, codeA], [, codeB]) => Number(codeA) - Number(codeB))
    .reduce<Record<string, z.ZodTypeAny>>((shape, [fieldKey]) => {
      shape[`singer_assessment_${fieldKey}`] = z
        .number({
          errorMap: () => ({
            message: translations.patientForms.fieldRequiredValidation,
          }),
        })
        .int()
        .min(0)
        .max(5);
      return shape;
    }, {});

  return z.object({
    // I. IDs
    insurance_ik_number: IKNumberSchema.nullish(),
    hospital_ik_number: IKNumberSchema.nullish(),

    // I. Allgemeine Angaben
    gender: z.number(),
    patient_first_name: z.string().nonempty().max(45),
    patient_last_name: z.string().nonempty().max(45),
    insurance_number: z.string().min(10).max(12),
    patient_birthdate: unixDateSchema,
    admission_date: unixDateSchema,
    discharge_date: unixDateSchema,
    early_rehabilitation_measures: predicamentSchema,
    rehab_admission_type: z.nativeEnum(REHAB_ADMISSION, {
      errorMap: () => ({
        message: translations.patientForms.generalForm.fieldRequired,
      }),
    }),
    rehab_admission_within_two_weeks_start_date: unixDateSchema.nullish(),
    rehab_admission_outside_two_weeks_start_date: unixDateSchema.nullish(),
    rehab_admission_outside_two_weeks_explanation: z
      .string()
      .max(600)
      .nullish(),

    // II. Rehabilitationsbedürftigkeit
    ...icdFields,
    surgery_performed: predicamentSchema,
    other_treatments: z.string().max(150).nullish(),
    complication_in_treatment_process: predicamentSchema,
    complication_cause_maindiagnosis_description: z.string().max(150).nullish(),
    complication_cause_cardiovascular_description: z
      .string()
      .max(150)
      .nullish(),
    complication_cause_other_description: z.string().max(150).nullish(),
    infections_state: predicamentYesNoUnknownSchema.nullish(),
    infections: z.string().max(INFECTIONS_CHARACTER_LIMIT).nullish(),

    // singer assessments
    singer_assessment_date: unixDateSchema,
    ...singerAssessmentFields,

    // III. Rehabilitationsfähigkeit
    limited_capacity: predicamentSchema,
    reha_capacity: z.nativeEnum(MUSCOSKELETAL_RESILIENCE).nullish(),
    reha_capacity_partial: z.preprocess(
      toNullishNumber,
      z
        .number({
          errorMap: () => ({
            message: translations.patientForms.medicalForm.errorWeightField,
          }),
        })
        .int()
        .max(999, {
          message: translations.patientForms.medicalForm.errorWeightField,
        })
        .nullish(),
    ),
    interim_prosthesis_fitted: predicamentSchema,
    reha_capacity_other: z.string().max(150).nullish(),

    // IV. Ziele/Prognose
    rehabilitation_goals: z.string().nonempty().max(600),
    rehab_focus: z.nativeEnum(REHAB_FOCUS, {
      errorMap: () => ({
        message: translations.patientForms.generalForm.fieldRequired,
      }),
    }),
    rehab_focus_other_description: z.string().max(600).nullish(),
    recommended_reha_type: z.nativeEnum(TYPE_OF_REHAB, {
      errorMap: () => ({
        message: translations.patientForms.generalForm.fieldRequired,
      }),
    }),
    recommended_reha_type_ambulant_mobil: z.string().max(150).nullish(),
    recommended_reha_type_stationary: z.string().max(150).nullish(),
    rehab_directive_state: predicamentSchema,
    rehab_directive_type: z
      .array(z.nativeEnum(REHAB_DIRECTIVE_TYPE))
      .max(12)
      .nullish(),

    // V. Special requirements
    special_requirements_required: predicamentSchema,
    special_requirements: z.array(z.nativeEnum(SPECIAL_REQUIREMENTS)).nullish(),
    special_requirements_decubitus_description: z
      .enum(["1", "2", "3", "4"], {
        errorMap: () => ({
          message:
            translations.patientForms.medicalForm.sectionSix
              .errorDecubitusStage,
        }),
      })
      .nullish(),
    special_requirements_dialysis_description: z.string().max(150).nullish(),
    special_requirements_other_description: z.string().max(600).nullish(),
    special_requirements_relatives_description: z
      .enum(["1", "2", "3", "4", "5"], {
        errorMap: () => ({
          message:
            translations.patientForms.medicalForm.sectionSix
              .errorRelativesField,
        }),
      })
      .nullish(),
    special_requirements_weight_bed_description: z.string().nullish(),

    // VI. Operations & Transport
    ...operationFields,
    transport_type: z.nativeEnum(TRANSPORTATION_TYPE, {
      errorMap: () => ({
        message: translations.patientForms.generalForm.fieldRequired,
      }),
    }),
    transport_type_taxi_specification: z
      .nativeEnum(TRANSPORTATION_SUBTYPE)
      .nullish(),
    transport_type_ambulance_specification: z
      .nativeEnum(TRANSPORTATION_SUBTYPE)
      .nullish(),
    require_accompanying_person: z.union([
      z.literal(PREDICAMENT_STATE_YES),
      z.literal(PREDICAMENT_STATE_NO),
    ]),
    doctor_in_charge_in_hospital_first_name: z.string().nonempty().max(45),
    doctor_in_charge_in_hospital_last_name: z.string().nonempty().max(45),
    doctor_in_charge_in_hospital_phone: z.string().nonempty().max(25),
    doctor_exam_date: unixDateSchema,
  });
}

function applyGKVMedicalFormRefinements(
  schema: GKVMedicalFormSchema,
  translations: Translations,
) {
  return schema.superRefine((data: GKVMedicalFormData, ctx) => {
    // Complications
    if (data.complication_in_treatment_process === PREDICAMENT_STATE_YES) {
      const main = isFilled(data.complication_cause_maindiagnosis_description);
      const cardio = isFilled(
        data.complication_cause_cardiovascular_description,
      );
      const other = isFilled(data.complication_cause_other_description);

      if (!main && !cardio && !other) {
        ctx.addIssue({
          path: ["complication_cause_maindiagnosis_description"],
          code: z.ZodIssueCode.custom,
          message:
            "Mindestens eine Beschreibung der Komplikation muss angegeben werden.",
        });
      }

      if (!isFilled(data.infections_state)) {
        ctx.addIssue({
          path: ["infections_state"],
          code: z.ZodIssueCode.custom,
          message: "Bitte geben Sie an, ob eine Keimbesiedelung vorliegt.",
        });
      }

      if (
        data.infections_state === PREDICAMENT_STATE_YES &&
        !isFilled(data.infections)
      ) {
        ctx.addIssue({
          path: ["infections"],
          code: z.ZodIssueCode.custom,
          message:
            "Bitte geben Sie eine Beschreibung der vorhandenen Keime an.",
        });
      }
    }

    // Verlegung validations
    if (
      data.rehab_admission_type === REHAB_ADMISSION.WITHIN_TWO_WEEKS &&
      !isFilled(data.rehab_admission_within_two_weeks_start_date)
    ) {
      ctx.addIssue({
        path: ["rehab_admission_within_two_weeks_start_date"],
        code: z.ZodIssueCode.custom,
        message: translations.patientForms.fieldRequiredValidation,
      });
    }

    if (
      data.rehab_admission_type === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS &&
      !isFilled(data.rehab_admission_outside_two_weeks_start_date)
    ) {
      ctx.addIssue({
        path: ["rehab_admission_outside_two_weeks_start_date"],
        code: z.ZodIssueCode.custom,
        message: translations.patientForms.fieldRequiredValidation,
      });
    }

    if (
      data.rehab_admission_type === REHAB_ADMISSION.OUTSIDE_TWO_WEEKS &&
      !isFilled(data.rehab_admission_outside_two_weeks_explanation)
    ) {
      ctx.addIssue({
        path: ["rehab_admission_outside_two_weeks_explanation"],
        code: z.ZodIssueCode.custom,
        message: translations.patientForms.fieldRequiredValidation,
      });
    }

    // Reha capcity
    if (data.reha_capacity === MUSCOSKELETAL_RESILIENCE.PARTIALLY) {
      if (!isFilled(data.reha_capacity_partial)) {
        ctx.addIssue({
          path: ["reha_capacity_partial"],
          code: z.ZodIssueCode.custom,
          message: "Bitte geben Sie die Teilbelastung in kg an.",
        });
      }
    }

    //  Fall nach § 16 Reha-RL gegeben
    if (
      data.rehab_directive_state === PREDICAMENT_STATE_YES &&
      (!data.rehab_directive_type || data.rehab_directive_type.length === 0)
    ) {
      ctx.addIssue({
        path: ["rehab_directive_type"],
        code: z.ZodIssueCode.custom,
        message: translations.patientForms.fieldRequiredValidation,
      });
    }

    // ICD Codes
    let completeGroupCount = 0;

    for (let i = 1; i <= 5; i++) {
      const diagnosis =
        data[`icd_code_${i}_diagnosis` as "icd_code_1_diagnosis"];
      const code = data[`icd_code_${i}_code` as "icd_code_1_code"];
      const reason = data[`icd_code_${i}_reason` as "icd_code_1_reason"];

      const diagnosisFilled = isFilled(diagnosis);
      const codeFilled = isFilled(code);
      const reasonFilled = isFilled(reason);

      const filled = [diagnosisFilled, codeFilled, reasonFilled].filter(
        Boolean,
      ).length;

      if (filled === 3) {
        completeGroupCount++;
      } else if (filled > 0 && filled < 3) {
        // Only show error on missing fields
        if (!diagnosisFilled) {
          ctx.addIssue({
            path: [`icd_code_${i}_diagnosis`],
            code: z.ZodIssueCode.custom,
            message: translations.patientForms.fieldRequiredValidation,
          });
        }
        if (!codeFilled) {
          ctx.addIssue({
            path: [`icd_code_${i}_code`],
            code: z.ZodIssueCode.custom,
            message: translations.patientForms.fieldRequiredValidation,
          });
        }
        if (!reasonFilled) {
          ctx.addIssue({
            path: [`icd_code_${i}_reason`],
            code: z.ZodIssueCode.custom,
            message: translations.patientForms.fieldRequiredValidation,
          });
        }
      }
    }

    if (completeGroupCount === 0) {
      // Add errors only to fields in the first row to simulate a form-level error
      if (!isFilled(data.icd_code_1_diagnosis)) {
        ctx.addIssue({
          path: ["icd_code_1_diagnosis"],
          code: z.ZodIssueCode.custom,
          message: translations.patientForms.fieldRequiredValidation,
        });
      }
      if (!isFilled(data.icd_code_1_code)) {
        ctx.addIssue({
          path: ["icd_code_1_code"],
          code: z.ZodIssueCode.custom,
          message: translations.patientForms.fieldRequiredValidation,
        });
      }
      if (!isFilled(data.icd_code_1_reason)) {
        ctx.addIssue({
          path: ["icd_code_1_reason"],
          code: z.ZodIssueCode.custom,
          message: translations.patientForms.fieldRequiredValidation,
        });
      }
    }

    // Transport
    if (
      data.transport_type === TRANSPORTATION_TYPE.TAXI &&
      !data.transport_type_taxi_specification
    ) {
      ctx.addIssue({
        path: ["transport_type_taxi_specification"],
        code: z.ZodIssueCode.custom,
        message: translations.patientForms.fieldRequiredValidation,
      });
    }
    if (
      data.transport_type === TRANSPORTATION_TYPE.AMBULANCE &&
      !data.transport_type_ambulance_specification
    ) {
      ctx.addIssue({
        path: ["transport_type_ambulance_specification"],
        code: z.ZodIssueCode.custom,
        message: translations.patientForms.fieldRequiredValidation,
      });
    }

    if (
      data.rehab_focus === REHAB_FOCUS.OTHER &&
      !isFilled(data.rehab_focus_other_description)
    ) {
      ctx.addIssue({
        path: ["rehab_focus_other_description"],
        code: z.ZodIssueCode.custom,
        message: "Bitte geben Sie den sonstigen Schwerpunkt an.",
      });
    }

    // Special Reha Requirements
    if (data.special_requirements_required === PREDICAMENT_STATE_YES) {
      const selected =
        Array.isArray(data.special_requirements) &&
        data.special_requirements.length > 0;

      if (!selected) {
        ctx.addIssue({
          path: ["special_requirements"],
          code: z.ZodIssueCode.custom,
          message:
            "Mindestens eine besondere Anforderung muss ausgewählt werden.",
        });
      }

      const selectedReqs = data.special_requirements ?? [];
      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.DIALYSIS) &&
        !isFilled(data.special_requirements_dialysis_description)
      ) {
        ctx.addIssue({
          path: ["special_requirements_dialysis_description"],
          code: z.ZodIssueCode.custom,
          message: "Bitte geben Sie das Dialyseverfahren an.",
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.OTHER) &&
        !isFilled(data.special_requirements_other_description)
      ) {
        ctx.addIssue({
          path: ["special_requirements_other_description"],
          code: z.ZodIssueCode.custom,
          message: "Bitte geben Sie die sonstige Anforderung an.",
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.RELATIVES) &&
        !isFilled(data.special_requirements_relatives_description)
      ) {
        ctx.addIssue({
          path: ["special_requirements_relatives_description"],
          code: z.ZodIssueCode.custom,
          message: "Bitte geben Sie den Pflegegrad des Angehörigen an.",
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.DECUBITUS) &&
        !isFilled(data.special_requirements_decubitus_description)
      ) {
        ctx.addIssue({
          path: ["special_requirements_decubitus_description"],
          code: z.ZodIssueCode.custom,
          message: "Bitte geben Sie den Grad des Dekubitus an.",
        });
      }

      if (
        selectedReqs.includes(SPECIAL_REQUIREMENTS.WEIGHT_BED) &&
        !isFilled(data.special_requirements_weight_bed_description)
      ) {
        ctx.addIssue({
          path: ["special_requirements_weight_bed_description"],
          code: z.ZodIssueCode.custom,
          message: "Bitte geben Sie das Gewichtslimit für das Bett an.",
        });
      }
    }

    // Surgeries
    let completeOperationCount = 0;

    for (const index of Object.values(OPERATION_INDEXES)) {
      const date = data[`operation_${index}_date`];
      const ops = data[`operation_${index}_ops`];
      const wound = data[`operation_${index}_wound_closed`];

      const hasDate = isFilled(date);
      const hasWound = isFilled(wound);
      const hasOPS = Array.isArray(ops) && ops.some(isFilled);

      const filledFields = [hasDate, hasWound, hasOPS].filter(Boolean).length;

      if (filledFields === 3) {
        completeOperationCount++;
      } else if (filledFields > 0) {
        // Only show field-level errors on missing ones
        if (!hasDate) {
          ctx.addIssue({
            path: [`operation_${index}_date`],
            code: z.ZodIssueCode.custom,
            message: translations.patientForms.fieldRequiredValidation,
          });
        }
        if (!hasWound) {
          ctx.addIssue({
            path: [`operation_${index}_wound_closed`],
            code: z.ZodIssueCode.custom,
            message: translations.patientForms.fieldRequiredValidation,
          });
        }
        if (!hasOPS) {
          ctx.addIssue({
            path: [`operation_${index}_ops`],
            code: z.ZodIssueCode.custom,
            message: translations.patientForms.fieldRequiredValidation,
          });
        }
      }

      // Validate each OPS code: must be a string and max 8 characters
      if (Array.isArray(ops)) {
        const messages: (string | null)[] = Array(ops.length).fill(null);

        ops.forEach((code, i) => {
          if (!isFilled(code)) return;

          if (typeof code !== "string") {
            messages[i] = "Each OPS code must be a string.";
          } else if (code.length > 8) {
            messages[i] = translations.patientForms.characterLimitValidation({
              numberMost: "8",
            });
          }
        });

        if (messages.some(Boolean)) {
          ctx.addIssue({
            path: [`operation_${index}_ops`],
            code: z.ZodIssueCode.custom,
            message: JSON.stringify({ type: PER_ITEM_ERRORS, messages }),
          });
        }
      }
    }

    // If surgery is marked as performed but no full operation is present
    if (
      data.surgery_performed === PREDICAMENT_STATE_YES &&
      completeOperationCount === 0
    ) {
      const firstIndex = OPERATION_INDEXES.OP1;

      const firstDate = data[`operation_${firstIndex}_date`];
      const firstOps = data[`operation_${firstIndex}_ops`];
      const firstWound = data[`operation_${firstIndex}_wound_closed`];

      if (!isFilled(firstDate)) {
        ctx.addIssue({
          path: [`operation_${firstIndex}_date`],
          code: z.ZodIssueCode.custom,
          message: translations.patientForms.fieldRequiredValidation,
        });
      }

      if (!isFilled(firstWound)) {
        ctx.addIssue({
          path: [`operation_${firstIndex}_wound_closed`],
          code: z.ZodIssueCode.custom,
          message: translations.patientForms.fieldRequiredValidation,
        });
      }

      if (!Array.isArray(firstOps) || !firstOps.some(isFilled)) {
        ctx.addIssue({
          path: [`operation_${firstIndex}_ops`],
          code: z.ZodIssueCode.custom,
          message: translations.patientForms.fieldRequiredValidation,
        });
      }
    }
  });
}

export function getGKVMedicalFormSchema({
  skipRequiredValidation,
  translations,
}: {
  skipRequiredValidation?: boolean;
  translations: Translations;
}) {
  const messages = createValidationMessages(translations);
  z.setErrorMap(createErrorMap<GKVMedicalFormSchema>(messages));

  const baseSchema = createBaseGKVMedicalFormSchema(translations);
  const partialSchema = baseSchema.partial();

  return skipRequiredValidation
    ? z.preprocess(stripEmptyFields, partialSchema)
    : applyGKVMedicalFormRefinements(baseSchema, translations);
}
